import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Receipt,
  ShoppingCart,
  FileText,
  Lightbulb,
  Home,
  Wrench,
  Briefcase,
  Car,
  Shield,
  Calculator,
  RotateCcw,
  Percent,
  DollarSign,
  AlertCircle,
  Gift,
  FileX,
} from 'lucide-react';

type CategoriaFactura = 'gasto' | 'compra' | 'nota_credito';

interface SubtipoFactura {
  id: string;
  nombre: string;
  descripcion: string;
  icon: React.ReactNode;
}

interface CategoriaConfig {
  label: string;
  color: string;
  icon: React.ReactNode;
  subtipos: SubtipoFactura[];
}

const categoriasConfig: Record<CategoriaFactura, CategoriaConfig> = {
  gasto: {
    label: 'Gasto',
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    icon: <Receipt className="h-4 w-4" />,
    subtipos: [
      {
        id: 'servicios_publicos',
        nombre: 'Servicios Públicos',
        descripcion: 'Agua, Luz, Gas, Internet, Teléfono',
        icon: <Lightbulb className="h-5 w-5 text-yellow-600" />,
      },
      {
        id: 'alquiler',
        nombre: 'Alquiler / Arrendamiento',
        descripcion: 'Renta de locales, oficinas, bodegas',
        icon: <Home className="h-5 w-5 text-blue-600" />,
      },
      {
        id: 'mantenimiento',
        nombre: 'Mantenimiento y Reparaciones',
        descripcion: 'Servicios de mantenimiento preventivo y correctivo',
        icon: <Wrench className="h-5 w-5 text-gray-600" />,
      },
      {
        id: 'gastos_oficina',
        nombre: 'Gastos de Oficina',
        descripcion: 'Papelería, útiles, suministros de oficina',
        icon: <Briefcase className="h-5 w-5 text-indigo-600" />,
      },
      {
        id: 'transporte',
        nombre: 'Transporte y Viáticos',
        descripcion: 'Gastos de viaje, hospedaje, alimentación',
        icon: <Car className="h-5 w-5 text-green-600" />,
      },
      {
        id: 'seguros',
        nombre: 'Seguros',
        descripcion: 'Pólizas de seguros empresariales',
        icon: <Shield className="h-5 w-5 text-purple-600" />,
      },
      {
        id: 'impuestos',
        nombre: 'Impuestos y Tasas',
        descripcion: 'Pagos de impuestos, tasas gubernamentales',
        icon: <Calculator className="h-5 w-5 text-red-600" />,
      },
    ],
  },
  compra: {
    label: 'Compra',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: <ShoppingCart className="h-4 w-4" />,
    subtipos: [], // Los items de compra se manejan con compraItems
  },
  nota_credito: {
    label: 'Nota de Crédito',
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: <FileText className="h-4 w-4" />,
    subtipos: [
      {
        id: 'devolucion',
        nombre: 'Devolución de Mercancía',
        descripcion: 'Devolución total o parcial de productos',
        icon: <RotateCcw className="h-5 w-5 text-blue-600" />,
      },
      {
        id: 'descuento_pronto_pago',
        nombre: 'Descuento por Pronto Pago',
        descripcion: 'Descuento aplicado por pago anticipado',
        icon: <Percent className="h-5 w-5 text-green-600" />,
      },
      {
        id: 'ajuste_precio',
        nombre: 'Ajuste de Precio',
        descripcion: 'Corrección de precio acordado',
        icon: <DollarSign className="h-5 w-5 text-yellow-600" />,
      },
      {
        id: 'error_facturacion',
        nombre: 'Error en Facturación',
        descripcion: 'Corrección por errores en la factura original',
        icon: <AlertCircle className="h-5 w-5 text-red-600" />,
      },
      {
        id: 'bonificacion',
        nombre: 'Bonificación',
        descripcion: 'Bonificaciones o incentivos comerciales',
        icon: <Gift className="h-5 w-5 text-purple-600" />,
      },
      {
        id: 'anulacion_parcial',
        nombre: 'Anulación Parcial',
        descripcion: 'Anulación parcial de la factura',
        icon: <FileX className="h-5 w-5 text-gray-600" />,
      },
    ],
  },
};

interface TipoFacturaSelectorProps {
  value?: string;
  onChange?: (value: string | undefined) => void;
}

export function TipoFacturaSelector({ value, onChange }: TipoFacturaSelectorProps) {
  const handleChange = (newValue: string) => {
    if (newValue === 'all') {
      onChange?.(undefined);
    } else {
      onChange?.(newValue);
    }
  };

  return (
    <Select
      value={value || 'all'}
      onValueChange={handleChange}
    >
      <SelectTrigger className="w-[180px]">
        <SelectValue placeholder="Tipos" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Tipos</SelectItem>
        <SelectItem value="gasto">
          <div className="flex items-center gap-2">
            <Receipt className="h-4 w-4 text-orange-600" />
            Gasto
          </div>
        </SelectItem>
        <SelectItem value="compra">
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-4 w-4 text-blue-600" />
            Compra
          </div>
        </SelectItem>
        <SelectItem value="nota_credito">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-green-600" />
            Nota de Crédito
          </div>
        </SelectItem>
      </SelectContent>
    </Select>
  );
}

// Items de compra con código y nombre
export interface CompraItem {
  codigo: string;
  nombre: string;
}

export const compraItems: CompraItem[] = [
  { codigo: 'S1000', nombre: 'ANTICIPOS' },
  { codigo: 'S1110', nombre: 'CAJA MENOR' },
  { codigo: 'S143501', nombre: 'MERCANCIAS NO PRODUCIDA POR LA EMPRESA' },
  { codigo: 'S146501', nombre: 'INVENTARIOS EN TRANSITO' },
  { codigo: 'S14650101', nombre: 'INGRESOS A TERCEROS' },
  { codigo: 'S14650102', nombre: 'INGRESOS PROPIOS GRAVADOS' },
  { codigo: 'S14650103', nombre: 'ARANCEL' },
  { codigo: 'S14650104', nombre: 'MERCANCIAS EN TRANSITO' },
  { codigo: 'S14650105', nombre: 'INGRESOS PROPIOS *NO GRAVADO' },
  { codigo: 'S152405', nombre: 'MUEBLES Y ENSERES' },
  { codigo: 'S15240501', nombre: 'IVA MAYOR VALOR MUEBLES Y ENSERES' },
  { codigo: 'S152805', nombre: 'EQUIPOS DE PROCESAMIENTO DE DATOS' },
  { codigo: 'S15280502', nombre: 'IVA MAYOR VR COMPRA DE EQUIPOS' },
  { codigo: 'S15400501', nombre: 'AUTOS, CAMIONETAS Y CAMPEROS' },
  { codigo: 'S15400502', nombre: 'IVA EN AUTOS, CAMIONETAS Y CAMPEROS' },
  { codigo: 'S15403001', nombre: 'MOTOCICLETAS' },
  { codigo: 'S15403002', nombre: 'IVA EN MOTOCICLETAS' },
  { codigo: 'S2000', nombre: 'AIU' },
  { codigo: 'S20001', nombre: 'TRANSPORTE INTERNACIONAL' },
  { codigo: 'S20002', nombre: 'SEGURO INTERNACIONAL' },
  { codigo: 'S21051001', nombre: 'CREDITO BBVA 9629242959' },
  { codigo: 'S21051003', nombre: 'BANCOLOMBIA 100104894' },
  { codigo: 'S23700501', nombre: 'PAGO EPS' },
  { codigo: 'S23700601', nombre: 'PAGO ARL' },
  { codigo: 'S23701001', nombre: 'PAGO ICBF, SENA Y CCF' },
  { codigo: 'S23703001', nombre: 'LIBRANZA' },
  { codigo: 'S23803001', nombre: 'PAGO FONDO DE PENSIONES' },
  { codigo: 'S24080204', nombre: 'IVA POR IMPORTACIONES' },
  { codigo: 'S281505', nombre: 'REINTEGRO AUXILIO PAEF' },
  { codigo: 'S41360501', nombre: 'DESCUENTOS COMERCIALES CONCEDIDOS' },
  { codigo: 'S42104001', nombre: 'DESCUENTOS PRONTO PAGO' },
  { codigo: 'S422595', nombre: 'COMISION POR INCENTIVOS' },
  { codigo: 'S425015', nombre: 'RECLAMOS' },
  { codigo: 'S425050', nombre: 'FERIA DE SALUD Y BELLEZA BOGOTA' },
  { codigo: 'S425051', nombre: 'PARTICIPACIÓN REINADO DE LA BELLEZA Y FERIA DE FLORES 2022' },
  { codigo: 'S425052', nombre: 'PENDONES Y COPAS AGUARDIENTERAS (PARTICIPACIÓN REINADO FERIA DE FLORES)' },
  { codigo: 'S425053', nombre: 'APOYO MERCADEO' },
  { codigo: 'S429581', nombre: 'AJUSTE AL PESO' },
  { codigo: 'S510506', nombre: 'SUELDOS' },
  { codigo: 'S510515', nombre: 'HORAS EXTRAS Y RECARGOS' },
  { codigo: 'S510518', nombre: 'COMISIONES' },
  { codigo: 'S510524', nombre: 'INCAPACIDADES' },
  { codigo: 'S510527', nombre: 'AUXILIO DE TRANSPORTE' },
  { codigo: 'S510530', nombre: 'CESANTIAS' },
  { codigo: 'S510533', nombre: 'INTERESES SOBRE CESANTIAS' },
  { codigo: 'S510536', nombre: 'PRIMA DE SERVICIOS' },
  { codigo: 'S510539', nombre: 'VACACIONES' },
  { codigo: 'S510548', nombre: 'BONIFICACIONES' },
  { codigo: 'S510551', nombre: 'DOTACION Y SUMINISTRO A TRABAJADORES GRAVADO' },
  { codigo: 'S51055101', nombre: 'DOTACION Y SUMINISTRO A TRABAJADORES NO GRAVADO' },
  { codigo: 'S510563', nombre: 'CAPACITACION AL PERSONAL' },
  { codigo: 'S510568', nombre: 'APORTES A A.R.P' },
  { codigo: 'S510569', nombre: 'APORTES A ENTIDADES PROMOTORAS DE SALUD' },
  { codigo: 'S510570', nombre: 'APORTES DE PENSION' },
  { codigo: 'S510572', nombre: 'APORTES CAJAS DE COMPENSACION FAMILIAR' },
  { codigo: 'S51059501', nombre: 'EXAMENES MEDICOS OCUPACIONALES' },
  { codigo: 'S51059502', nombre: 'BENEFICIOS A EMPLEADOS' },
  { codigo: 'S511010', nombre: 'REVISORIA FISCAL' },
  { codigo: 'S51102501', nombre: 'ASESORIA JURIDICA GRAVADO' },
  { codigo: 'S511030', nombre: 'ASESORIA FINANCIERA' },
  { codigo: 'S511035', nombre: 'ASESORIA TECNICA' },
  { codigo: 'S51103501', nombre: 'SEGURIDAD EN EL TRABAJO GRAVADO' },
  { codigo: 'S511505', nombre: 'INDUSTRIA Y COMERCIO' },
  { codigo: 'S51154001', nombre: 'IMPUESTO VEHICULAR' },
  { codigo: 'S511570', nombre: 'IVA DESCONTABLE' },
  { codigo: 's51152001', nombre: 'IMP ASUMIDO 1' },
  { codigo: 's52152001', nombre: 'IMP ASUMIDO 2' },
  { codigo: 'S512010', nombre: 'CONSTRUCCIONES Y EDIFICACIONES' },
  { codigo: 'S51204001', nombre: 'FLOTA Y EQUIPO DE TRANSPORTE *GRAVADO' },
  { codigo: 'S51204002', nombre: 'FLOTA Y EQUIPO DE TRANSPORTE *NO GRAVADO' },
  { codigo: 'S512095', nombre: 'OTROS' },
  { codigo: 'S51209501', nombre: 'ARRIENDO OFIMAPYME' },
  { codigo: 'S512505', nombre: 'CONTRIBUCIONES' },
  { codigo: 'S512510', nombre: 'AFILIACIONES Y SOSTENIMIENTO GRAVADO' },
  { codigo: 'S513010', nombre: 'CUMPLIMIENTO' },
  { codigo: 'S51306001', nombre: 'RESPONSABILIDAD CIVIL Y EXTRACONTRACTUAL' },
  { codigo: 'S51307501', nombre: 'OBLIGATORIO ACCIDENTE DE TRÁNSITO' },
  { codigo: 'S513505', nombre: 'ASEO Y VIGILANCIA' },
  { codigo: 'S513510', nombre: 'TEMPORALES' },
  { codigo: 'S513515', nombre: 'ASISTENCIA TECNICA GRAVADO' },
  { codigo: 'S51351501', nombre: 'ASISTENCIA TECNICA NO GRAVADO' },
  { codigo: 'S513520', nombre: 'PROCESAMIENTO ELECTRONICO DE DATOS GRAVADO' },
  { codigo: 'S513525', nombre: 'ACUEDUCTO Y ALCANTARILLADO' },
  { codigo: 'S513530', nombre: 'ENERGIA ELECTRICA' },
  { codigo: 'S513535', nombre: 'TELEFONIA GRAVADA' },
  { codigo: 'S513550', nombre: 'TRANSPORTE, FLETES Y ACARREOS' },
  { codigo: 'S513560', nombre: 'PUBLICIDAD, PROPAGANDA Y PROMOCION' },
  { codigo: 'S513595', nombre: 'OTROS' },
  { codigo: 'S51359501', nombre: 'INTERNET GRAVADO' },
  { codigo: 'S51359502', nombre: 'ADMINISTRACION NO GRAVADO' },
  { codigo: 'S51359503', nombre: 'IMPUESTO TELEFONICO' },
  { codigo: 'S51359504', nombre: 'SEGURIDAD EN EL TRABAJO GRAVADO' },
  { codigo: 'S51359505', nombre: 'OTROS SERVICIOS DEDUCIBLES' },
  { codigo: 'S51359506', nombre: 'OTROS SERVICIOS NO DEDUCIBLES' },
  { codigo: 'S514005', nombre: 'NOTARIALES' },
  { codigo: 'S514010', nombre: 'REGISTRO MERCANTIL NO GRAVADO' },
  { codigo: 'S51401001', nombre: 'REGISTRO MERCANTIL GRAVADO' },
  { codigo: 'S514015', nombre: 'TRAMITES Y LICENCIAS' },
  { codigo: 'S514095', nombre: 'OTROS' },
  { codigo: 'S514515', nombre: 'MAQUINARIA Y EQUIPO' },
  { codigo: 'S514520', nombre: 'MANTENIMIENTO EQUIPO DE OFICINA' },
  { codigo: 'S514525', nombre: 'MANTENIMIENTO EQUIPO DE COMPUTACIÓN Y COMUNICACIÓN' },
  { codigo: 'S514540', nombre: 'MANTENIMIENTO FLOTA Y EQUIPO DE TRANSPORTE' },
  { codigo: 'S515005', nombre: 'INSTALACIONES ELECTRICAS' },
  { codigo: 'S515015', nombre: 'REPARACIONES LOCATIVAS' },
  { codigo: 'S515095', nombre: 'OTROS ADECUACIÓN E INSTALACIÓN' },
  { codigo: 'S519520', nombre: 'GASTOS DE REPRESENTACION Y RELACIONES PUBLICAS' },
  { codigo: 'S519525', nombre: 'ELEMENTOS DE ASEO Y CAFETERIA GRAVADO 19%' },
  { codigo: 'S51952501', nombre: 'ELEMENTOS DE ASEO Y CAFETERIA NO GRAVADO' },
  { codigo: 'S51952502', nombre: 'ELEMENTOS DE ASEO Y CAFETERIA GRAVADO 5%' },
  { codigo: 'S519530', nombre: 'UTILES, PAPELERIA Y FOTOCOPIAS GRAVADO' },
  { codigo: 'S51953001', nombre: 'UTILES, PAPELERIA Y FOTOCOPIAS NO GRAVADO' },
  { codigo: 'S51953501', nombre: 'COMBUSTIBLES Y LUBRICANTES' },
  { codigo: 'S519560', nombre: 'CASINO Y RESTAURANTE' },
  { codigo: 'S519581', nombre: 'AJUSTE AL PESO' },
  { codigo: 'S519595', nombre: 'OTROS DEDUCIBLES' },
  { codigo: 'S51959503', nombre: 'OTROS NO DEDUCIBLES' },
  { codigo: 'S520551', nombre: 'DOTACION Y SUMINISTRO A TRABAJADORES VENTAS GRAVADO' },
  { codigo: 'S52055101', nombre: 'DOTACION Y SUMINISTRO A TRABAJADORES VENTAS NO GRAVADO' },
  { codigo: 'S520563', nombre: 'CAPACITACION AL PERSONAL NO GRAVADO' },
  { codigo: 'S52056301', nombre: 'CAPACITACION AL PERSONAL GRAVADO' },
  { codigo: 'S520566', nombre: 'GASTOS DEPORTIVOS Y DE RECREACIÓN *GRAVADO' },
  { codigo: 'S52056601', nombre: 'GASTOS DEPORTIVOS Y DE RECREACIÓN *NO GRAVADO' },
  { codigo: 'S52059501', nombre: 'EXAMENES MEDICOS OCUPACIONALES' },
  { codigo: 'S52059502', nombre: 'POLIGRAFIA' },
  { codigo: 'S521035', nombre: 'ASESORIA TECNICA' },
  { codigo: 'S52109501', nombre: 'HONORARIOS MERCADEO' },
  { codigo: 'S522010', nombre: 'ARRIENDO ESPACIOS NO GRAVADO' },
  { codigo: 'S52201001', nombre: 'CONSTRUCCIONES Y EDIFICACIONES GRAVADO' },
  { codigo: 'S522015', nombre: 'ARRIENDO MAQUINARIA Y EQUIPO' },
  { codigo: 'S52209501', nombre: 'ALQUILER MOBILIARIO' },
  { codigo: 'S52209502', nombre: 'ALQUILER DE PLATAFORMA ECOMMERCE' },
  { codigo: 'S522510', nombre: 'AFILIACIONES Y SOSTENIMIENTO GRAVADO' },
  { codigo: 'S52251002', nombre: 'AFILIACIONES Y SOSTENIMIENTO NO GRAVADO' },
  { codigo: 'S52301001', nombre: 'CUMPLIMIENTO' },
  { codigo: 'S52306001', nombre: 'RESPONSABILIDAD CIVIL Y EXTRACONTRACTUAL' },
  { codigo: 'S523505', nombre: 'ASEO Y VIGILANCIA' },
  { codigo: 'S523510', nombre: 'TEMPORALES' },
  { codigo: 'S52351001', nombre: 'TEMPORALES GRAVADO' },
  { codigo: 'S52351002', nombre: 'TEMPORALES NO GRAVADO' },
  { codigo: 'S52351003', nombre: 'TEMPORALES NO DEDUCIBLE' },
  { codigo: 'S523515', nombre: 'AISTENCIA TECNICA GRAVADO' },
  { codigo: 'S52351501', nombre: 'AISTENCIA TECNICA NO GRAVADO' },
  { codigo: 'S523520', nombre: 'PROCESAMIENTO ELECTRONICO DE DATOS GRAVADO' },
  { codigo: 'S52352002', nombre: 'PROCESAMIENTO ELECTRONICO DE DATOS NO GRAVADO' },
  { codigo: 'S523530', nombre: 'ENERGIA ELECTRICA' },
  { codigo: 'S523535', nombre: 'TELEFONO' },
  { codigo: 'S52354001', nombre: 'CORREO, PORTES Y TELEGRAMAS NO GRAVADO' },
  { codigo: 'S523550', nombre: 'TRANSPORTE, FLETES Y ACARREOS *NO GRAVADO' },
  { codigo: 'S52355001', nombre: 'TRANSPORTE, FLETES Y ACARREOS *GRAVADO' },
  { codigo: 'S523560', nombre: 'PUBLICIDAD, PROPAGANDA Y PROMOCION GRAVADO' },
  { codigo: 'S52356001', nombre: 'PUBLICIDAD, PROPAGANDA Y PROMOCION NO GRAVADO' },
  { codigo: 'S523595', nombre: 'OTROS' },
  { codigo: 'S52359501', nombre: 'INTERNET' },
  { codigo: 'S52359502', nombre: 'ADMINISTRACION' },
  { codigo: 'S52359503', nombre: 'IMPUESTO TELEFONICO' },
  { codigo: 'S52359504', nombre: 'OTROS GRAVADO 19%' },
  { codigo: 'S52359505', nombre: 'GESTION INFORMATICA NO GRAVADO' },
  { codigo: 'S52359506', nombre: 'OTROS NO GRAVADO' },
  { codigo: 'S52359507', nombre: 'OTROS GRAVADO 5%' },
  { codigo: 'S52401001', nombre: 'REGISTRO MERCANTIL NO GRAVADO' },
  { codigo: 'S524015', nombre: 'TRAMITES Y LICENCIAS NO GRAVADO' },
  { codigo: 'S52401501', nombre: 'TRAMITES Y LICENCIAS GRAVADO' },
  { codigo: 'S524515', nombre: 'MAQUINARIA Y EQUIPO' },
  { codigo: 'S52451501', nombre: 'MAQUINARIA Y EQUIPO *NO GRAVADO' },
  { codigo: 'S525005', nombre: 'INSTALACIONES ELÉCTRICAS' },
  { codigo: 'S52509501', nombre: 'OTROS *GRAVADO' },
  { codigo: 'S52550501', nombre: 'ALOJAMIENTO Y MANUTENCION GRAVADO' },
  { codigo: 'S52550502', nombre: 'ALOJAMIENTO Y MANUTENCION NO GRAVADO' },
  { codigo: 'S52551501', nombre: 'PASAJES AEREOS GRAVADO' },
  { codigo: 'S52551502', nombre: 'PASAJES AEREOS NO GRAVADO' },
  { codigo: 'S52559501', nombre: 'PEAJES' },
  { codigo: 'S529505', nombre: 'COMISIONES GRAVADO' },
  { codigo: 'S52950501', nombre: 'COMISIONES NO GRAVADO' },
  { codigo: 'S529520', nombre: 'GASTOS DE REPRESENTACION' },
  { codigo: 'S52952501', nombre: 'ELEMENTOS DE ASEO Y CAFETERIA GRAVADO 19%' },
  { codigo: 'S52952502', nombre: 'ELEMENTOS DE ASEO Y CAFETERIA GRAVADO 5%' },
  { codigo: 'S529530', nombre: 'UTILES, PAPELERIA Y FOTOCOPIAS GRAVADO' },
  { codigo: 'S52953001', nombre: 'UTILES, PAPELERIA Y FOTOCOPIAS NO GRAVADO' },
  { codigo: 'S52953501', nombre: 'COMBUSTIBLES Y LUBRICANTES' },
  { codigo: 'S52954501', nombre: 'TAXIS Y BUSES' },
  { codigo: 'S52956001', nombre: 'CASINO Y RESTAURANTE DEDUCIBLE NO GRAVADO' },
  { codigo: 'S52956002', nombre: 'CASINO Y RESTAURANTE NO DEDUCIBLE NO GRAVADO' },
  { codigo: 'S52956003', nombre: 'CASINO Y RESTAURANTE DEDUCIBLE GRAVADO' },
  { codigo: 'S52956501', nombre: 'PARQUEADEROS' },
  { codigo: 'S529595', nombre: 'OTROS DEDUCIBLES GRAVADO' },
  { codigo: 'S52959502', nombre: 'OTROS DEDUCIBLES NO GRAVADO' },
  { codigo: 'S52959503', nombre: 'OTROS DEDUCIBLES NO GRAVADO OTROS NO DEDUCIBLES *NO GRAVADO' },
  { codigo: 'S530505', nombre: 'GASTOS BANCARIOS' },
  { codigo: 'S530515', nombre: 'COMISIONES *GRAVADO' },
  { codigo: 'S53051501', nombre: 'COMISIONES *NO GRAVADO' },
  { codigo: 'S530520', nombre: 'INTERESES' },
  { codigo: 'S53052001', nombre: 'INTERESES CORRIENTES' },
  { codigo: 'S53052002', nombre: 'INTERESES DE MORA' },
  { codigo: 'S53053501', nombre: 'DESCUENTOS COMERCIALES CONCEDIDOS' },
  { codigo: 'S531520', nombre: 'IMPUESTOS ASUMIDOS' },
  { codigo: 'S53952001', nombre: 'SANCIONES IMPORTACION' },
  { codigo: 'S539525', nombre: 'DONACIONES' },
  { codigo: 'S53959502', nombre: 'IVA MAYOR VALOR DEL GASTO' },
  { codigo: 'S99991', nombre: 'MERCANCIA BET' },
  { codigo: 'S99992', nombre: 'MERCANCIA BODEGAS DEL JAPON GRAVADO' },
  { codigo: 'S99993', nombre: 'MERCANCIA BET SHOP GRAVADO' },
  { codigo: 'S99994', nombre: 'MERCANCIA LA PULGA' },
  { codigo: 'S99995', nombre: 'REEMBOLSO PUBLICIDAD' },
  { codigo: 'S99996', nombre: 'MERCANCIA BET SHOP NO GRAVADO' },
  { codigo: 'S99997', nombre: 'MERCANCIA BODEGAS DEL JAPON NO GRAVADO' },
  { codigo: 'S99998', nombre: 'MERCANCIA TOSCANA GRAVADO' },
  { codigo: 'S99999', nombre: 'SERVICIO DE TRANSPORTES Y FLETES' },
];

// Exportar tipos y configuración para uso externo
export type { CategoriaFactura, SubtipoFactura };
export { categoriasConfig };
